package org.vreo.devicehub.features.device.data.network

import io.ktor.client.HttpClient
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.get
import io.ktor.http.ParametersBuilder
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlin.io.encoding.Base64
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.data.protocolBuffer.OutgoingProtoBufMessage
import org.vreo.devicehub.features.core.data.protocolBuffer.protoBufMessageToLocalModel
import org.vreo.devicehub.features.core.data.safeCall
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.device.data.dto.message.ResponseMessageDto
import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage
import org.vreo.devicehub.features.core.domain.device.message.AnprMessageImageRequestDto
import org.vreo.devicehub.features.core.domain.device.message.AnprMessagesRequestDto


private const val TAG = "KtorRemoteMessageDataSource"

/**
 * A data source for fetching messages from a remote API.
 *
 * @param httpClient The HTTP client used for making API requests.
 * @param hostUrl The base URL of the remote API.
 * @param messagePath The endpoint for fetching messages.
 * @param urlProtocol The protocol for the URL (e.g., HTTPS).
 */
class KtorRemoteMessageDataSource(
    private val httpClient: HttpClient,
    private val hostUrl: String,
    private val messagePath: String,
    private val imagesPath: String,
    private val urlProtocol: URLProtocol = URLProtocol.HTTPS
) {
    /**
     * Retrieves a list of AnprMessages from the remote API.
     *
     * @param token JWT authentication token for authenticated user
     * @param request request containing device credentials and query parameters
     * @return Result of list of messages [AnprCameraMessage] on success or error on failure
     */
    suspend fun getAnprMessages(
        token: String,
        request: AnprMessagesRequestDto
    ): Result<List<AnprCameraMessage>, DataError.Remote> {
        val results = getResponseMessages(
            token = token,
            request = request
        )

        return when (results) {
            is Result.Success -> {
                val messages = coroutineScope {
                    results.data.map { msgWrapper ->
                        async {
                            runCatching {
                                // Get message from response as local data model
                                val decodedMessage = protoBufMessageToLocalModel(
                                    byteArray = Base64.decode(msgWrapper.body)
                                )

                                val message = when (decodedMessage) {
                                    is Result.Success -> decodedMessage.data
                                    is Result.Error -> null
                                }

                                // Verify message type
                                if (message is OutgoingProtoBufMessage.AnprMessageMessage) {
                                    message.data
                                } else {
                                    AppLogger.info(
                                        tag = TAG,
                                        message = "Received message is not an AnprMessage"
                                    )
                                    null
                                }
                            }
                                .onFailure { error ->
                                    AppLogger.error(
                                        "Failed to decode message, error: ${error.message}"
                                    )
                                }
                                .getOrNull()
                        }
                    }.awaitAll().filterNotNull()
                }
                Result.Success(messages)
            }

            is Result.Error -> {
                Result.Error(results.error)
            }
        }
    }

    /**
     * Retrieves an image from the remote API.
     *
     * @param token JWT authentication token for authenticated user
     * @param request request image path
     * @return Result of image bytes on success or error on failure
     */
    suspend fun getImage(
        token: String,
        request: AnprMessageImageRequestDto
    ): Result<ByteArray, DataError.Remote> {
        val queryParameters = ParametersBuilder()
        queryParameters.append(AnprMessageImageRequestDto::path.name, request.path)

        return safeCall<ByteArray>(
            execute = {
                httpClient.get {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(imagesPath)
                        parameters.appendAll(queryParameters.build())
                    }
                    bearerAuth(token)
                }
            }
        )
    }

    /**
     * Retrieves a list of messages from the remote API, those are actual messages (row)
     * stored in cloud database.
     *
     * @param token JWT authentication token for authenticated user
     * @param request request containing device credentials and query parameters
     * @return Result of list of messages [ResponseMessageDto] on success or error on failure
     */
    private suspend fun getResponseMessages(
        token: String,
        request: AnprMessagesRequestDto
    ): Result<List<ResponseMessageDto>, DataError.Remote> {
        val queryParameters = ParametersBuilder()
        queryParameters.append(AnprMessagesRequestDto::deviceId.name, request.deviceId)
        queryParameters.append(AnprMessagesRequestDto::limit.name, request.limit.toString())

        request.fromTime?.let {
            queryParameters.append(AnprMessagesRequestDto::fromTime.name, it.toString())
        }

        request.toTime?.let {
            queryParameters.append(AnprMessagesRequestDto::toTime.name, it.toString())
        }

        request.ignoreIds?.forEach { id ->
            queryParameters.append(AnprMessagesRequestDto::ignoreIds.name, "'$id'")
        }

        return safeCall<List<ResponseMessageDto>>(
            execute = {
                httpClient.get {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(messagePath)
                        parameters.appendAll(queryParameters.build())
                    }
                    bearerAuth(token)
                }
            }
        )
    }
}